import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { authApi, type AuthUser } from "../api/auth";
import { ApiError, setTenantOverride, getTenantOverride } from "../api/client";
import { useAssistantStore } from "./assistant";

/**
 * F22 — wipe the AI-assistant conversation (persisted in localStorage) on logout
 * and tenant-switch, so a shared workstation never leaks the previous user's (or
 * previous tenant's) chat to whoever logs in next.
 */
function clearAssistantState(): void {
  try { useAssistantStore().clear(); } catch { /* pinia not active (e.g. unit env) */ }
  try {
    localStorage.removeItem("cdp-assistant-chat-v1");
    localStorage.removeItem("cdp-assistant-open-v1");
  } catch { /* localStorage disabled — non-fatal */ }
}

/**
 * LocalStorage flag set on every successful login and cleared on explicit
 * logout. Lets the bootstrap distinguish between "fresh visit" (no flag, no
 * cookie) and "session expired" (flag present, cookie rejected by server).
 * Used by the router guard to add `?expired=1` to the /login redirect so
 * LoginPage shows the «Сессия истекла» banner.
 */
const HAD_SESSION_KEY = "cdp_had_session";

function safeReadFlag(): boolean {
  try { return localStorage.getItem(HAD_SESSION_KEY) === "1"; } catch { return false; }
}
function safeWriteFlag(v: boolean): void {
  try {
    if (v) localStorage.setItem(HAD_SESSION_KEY, "1");
    else localStorage.removeItem(HAD_SESSION_KEY);
  } catch { /* localStorage disabled — non-fatal */ }
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(null);
  const bootstrapped = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);
  /** Set true when bootstrap's /v1/auth/me returns 401 while had-session flag was set. */
  const sessionExpired = ref(false);
  /** When a password login needs a second factor: the one-time MFA challenge token
   *  (no session issued yet). LoginPage shows the code step while this is set. */
  const mfaPending = ref<{ token: string } | null>(null);

  const isAuthenticated = computed(() => user.value !== null);
  const role = computed(() => user.value?.role ?? null);
  const isSuperAdmin = computed(() => role.value === "super_admin");

  /** Effective fine-grained permission set (RBAC Block B6). */
  const permissions = computed<ReadonlySet<string>>(() => new Set(user.value?.permissions ?? []));

  /**
   * Fine-grained permission check. Pass a canonical `resource:action` permission
   * (e.g. `auth.can("profiles:unmask_pii")` or `auth.can("roles", "create")`).
   * Prefer this over role checks for feature gating so custom roles work.
   *
   * Back-compat: if the server didn't send `permissions` (older build), fall back
   * to the coarse role so nothing silently locks out.
   */
  function can(resource: string, action?: string): boolean {
    const perm = action ? `${resource}:${action}` : resource;
    if (user.value?.permissions && user.value.permissions.length > 0) {
      return permissions.value.has(perm);
    }
    // Fallback (F24): the server now ALWAYS sends `permissions` (RBAC Block B6),
    // so this branch is only reached against a pre-B6 backend. Keep it as a thin
    // safety net for super_admin ONLY — never grant a tenant_admin phantom broad
    // access from a missing permission list (the old fallback over-granted).
    return role.value === "super_admin";
  }

  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return;
    try {
      user.value = await authApi.me();
      sessionExpired.value = false;
      // A restored session counts as "had a session" — needed for SSO logins,
      // which establish the cookie via the BFF callback and never go through
      // login()/verifyMfa(). Without this the «Сессия истекла» heuristic would
      // miss a later expiry of a federated session.
      safeWriteFlag(true);
    } catch (e) {
      user.value = null;
      // 401 + previously-logged-in = session-was-revoked. We don't know for
      // sure the cookie was sent (HttpOnly hides it from JS), but the local
      // "had session" flag is a good proxy. Fresh visits don't trigger this.
      if (e instanceof ApiError && e.status === 401 && safeReadFlag()) {
        sessionExpired.value = true;
      }
    } finally {
      bootstrapped.value = true;
    }
  }

  /**
   * Submit the first factor (password). Resolves to { mfaRequired: true } when the
   * account has MFA on — the caller then collects a code and calls verifyMfa().
   * Otherwise the session is established and the user is set.
   */
  async function login(loginValue: string, password: string): Promise<{ mfaRequired: boolean }> {
    loading.value = true;
    error.value = null;
    try {
      const r = await authApi.login(loginValue, password);
      if ("mfaRequired" in r) {
        mfaPending.value = { token: r.mfaToken };
        return { mfaRequired: true };
      }
      user.value = r.user;
      mfaPending.value = null;
      sessionExpired.value = false;
      safeWriteFlag(true);
      return { mfaRequired: false };
    } catch (e) {
      error.value = e instanceof ApiError
        ? (e.code === "INVALID_CREDENTIALS" ? "Неверный логин или пароль" : e.message)
        : "Не удалось войти";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  /** Finish a pending MFA login with a TOTP or backup code. */
  async function verifyMfa(code: string): Promise<void> {
    if (!mfaPending.value) throw new Error("Нет ожидающей проверки MFA");
    loading.value = true;
    error.value = null;
    try {
      user.value = await authApi.verifyMfa(mfaPending.value.token, code);
      mfaPending.value = null;
      sessionExpired.value = false;
      safeWriteFlag(true);
    } catch (e) {
      error.value = e instanceof ApiError
        ? (e.code === "MFA_BAD_CODE" ? "Неверный код"
          : e.code === "MFA_CHALLENGE_INVALID" ? "Время подтверждения истекло — войдите заново"
          : e.message)
        : "Не удалось подтвердить код";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  /** Abandon a pending MFA login (back to the password form). */
  function cancelMfa(): void {
    mfaPending.value = null;
    error.value = null;
  }

  async function logout(): Promise<void> {
    try { await authApi.logout(); } catch {}
    user.value = null;
    sessionExpired.value = false;
    safeWriteFlag(false);
    // Clear any persisted tenant override — next user might be different role.
    setTenantOverride(null);
    // F22 — drop the persisted assistant chat (shared-workstation leak).
    clearAssistantState();
  }

  /**
   * Super_admin: switch the effective tenant for subsequent API calls.
   * Pass `null` to revert to the user's home tenant.
   *
   * Side effects:
   *   - X-Tenant-Override header attached by axios interceptor on every request
   *   - localStorage persists choice across page reloads
   *   - /v1/auth/me refetched so `user.tenant_id`/`tenant_slug` reflect the new tenant
   *   - returns the refetched user so callers can react synchronously
   */
  async function switchTenant(slug: string | null): Promise<AuthUser> {
    if (!isSuperAdmin.value) throw new Error("switchTenant requires super_admin role");
    setTenantOverride(slug);
    const me = await authApi.me();
    user.value = me;
    // F22 — the previous tenant's chat must not carry into the new tenant context.
    clearAssistantState();
    return me;
  }

  const currentTenantOverride = computed<string | null>(() => {
    const slug = getTenantOverride();
    if (!slug) return null;
    // Hide override marker when it matches the home tenant (no-op case).
    if (user.value?.home_tenant_id && user.value.home_tenant_id === user.value.tenant_id) return null;
    return slug;
  });

  return {
    user, bootstrapped, loading, error, sessionExpired, mfaPending,
    isAuthenticated, role, isSuperAdmin, permissions, can,
    bootstrap, login, verifyMfa, cancelMfa, logout,
    switchTenant, currentTenantOverride,
  };
});
